import type { Disposable } from "@/shared/contracts/lifecycle";
import { HoverIntentController } from "@/features/hoverIdle/core/hoverIntentController";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";

const DEFAULT_MP_CHARGE_MS = 2000;

export interface MpSlotChargeControllerOptions {
  readonly element: HTMLElement;
  readonly stateElement?: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly chargeMs?: number;
  // Resolved each time a new charge starts, so settings changes apply to the
  // next hover without rebuilding the controller. Falls back to `chargeMs`
  // (and ultimately `DEFAULT_MP_CHARGE_MS`) when omitted.
  readonly getChargeMs?: () => number;
  readonly onAssign: () => void;
  readonly onRemove: () => void;
}

export interface MpSlotChargeState {
  readonly isAssigned: boolean;
  readonly canAssign: boolean;
  readonly canRemove: boolean;
}

export class MpSlotChargeController implements Disposable {
  private readonly stateElement: HTMLElement;
  private readonly intent: HoverIntentController;
  private readonly chargeMs: number;
  private readonly getChargeMs?: () => number;
  private readonly onAssign: () => void;
  private readonly onRemove: () => void;
  private isAssigned = false;
  private canAssign = false;
  private canRemove = false;
  private inputLocked = false;
  private chargeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: MpSlotChargeControllerOptions) {
    this.stateElement = options.stateElement ?? options.element;
    this.chargeMs = options.chargeMs ?? DEFAULT_MP_CHARGE_MS;
    this.getChargeMs = options.getChargeMs;
    this.onAssign = options.onAssign;
    this.onRemove = options.onRemove;
    this.stateElement.dataset.state = "empty";
    this.intent = new HoverIntentController({
      target: options.element,
      pointerModel: options.pointerModel,
      onHoverChange: (isHovering) => this.handleHoverChange(isHovering),
    });
  }

  sync(state: MpSlotChargeState): void {
    if (this.isAssigned !== state.isAssigned) {
      this.cancelCharge();
      this.isAssigned = state.isAssigned;
    }
    this.canAssign = state.canAssign;
    this.canRemove = state.canRemove;
    if (this.chargeTimer !== null && !this.canContinueCharge()) {
      this.cancelCharge();
    }
    if (this.chargeTimer === null) {
      this.stateElement.dataset.state = this.isAssigned ? "filled" : "empty";
    }
  }

  setInputLocked(locked: boolean): void {
    if (locked === this.inputLocked) {
      return;
    }
    this.inputLocked = locked;
    if (locked) {
      this.intent.clearPointerHover();
      this.cancelCharge();
    }
  }

  cancelCharge(): void {
    if (this.chargeTimer !== null) {
      clearTimeout(this.chargeTimer);
      this.chargeTimer = null;
    }
    this.stateElement.dataset.state = this.isAssigned ? "filled" : "empty";
  }

  clearPointerHover(): void {
    this.intent.clearPointerHover();
  }

  refresh(options?: { readonly forceEmit?: boolean }): void {
    this.intent.refresh(options);
  }

  dispose(): void {
    if (this.chargeTimer !== null) {
      clearTimeout(this.chargeTimer);
      this.chargeTimer = null;
    }
    this.intent.dispose();
  }

  private canContinueCharge(): boolean {
    return this.isAssigned ? this.canRemove : this.canAssign;
  }

  private handleHoverChange(isHovering: boolean): void {
    if (!isHovering) {
      this.cancelCharge();
      return;
    }
    if (this.inputLocked) {
      this.cancelCharge();
      return;
    }
    if (!this.canContinueCharge()) {
      return;
    }
    if (this.chargeTimer !== null) {
      return;
    }
    const chargeMs = this.getChargeMs?.() ?? this.chargeMs;
    this.stateElement.style.setProperty(
      "--hover-toggle-charge-duration",
      `${chargeMs}ms`,
    );
    this.stateElement.dataset.state = this.isAssigned
      ? "retrieving"
      : "charging";
    this.chargeTimer = setTimeout(() => {
      this.chargeTimer = null;
      if (this.inputLocked || !this.canContinueCharge()) {
        this.stateElement.dataset.state = this.isAssigned ? "filled" : "empty";
        return;
      }
      if (this.isAssigned) {
        this.onRemove();
      } else {
        this.onAssign();
      }
    }, chargeMs);
  }
}
